import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { SHOP_PUBLIC_COLUMNS } from '../lib/shopColumns';
import PaymentStep from '../components/PaymentStep';
import { isValidUKMobile } from '../lib/phone';
import { resolveSubdomainFromUrl } from '../lib/resolveShop';
import { resolveAccent, normaliseTheme } from '../themes';
import { resolveDeposit } from '../lib/deposit';
import { buildDayAvailability, buildBarberNextAvailable } from '../lib/booking/availability';
import { dateKey } from '../lib/availability';
import { StepsHeader } from '../components/booking/StepsHeader';
import { BookingHeader } from '../components/booking/BookingHeader';
import { ServicesStep } from '../components/booking/ServicesStep';
import { BarbersStep } from '../components/booking/BarbersStep';
import { TimeStep } from '../components/booking/TimeStep';
import { DetailsStep } from '../components/booking/DetailsStep';
import { SettlingScreen, ConfirmedScreen } from '../components/booking/StatusScreens';
import './Booking.css';

const Booking = () => {
  const { subdomain: pathSubdomain } = useParams();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [shop, setShop] = useState(null);
  const [shopNotFound, setShopNotFound] = useState(false);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [dayAvailability, setDayAvailability] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [emptyReason, setEmptyReason] = useState(null);
  const [barberNext, setBarberNext] = useState({});
  const [barberNextLoading, setBarberNextLoading] = useState(false);
  const [customerData, setCustomerData] = useState({ name: '', phone: '', email: '', isNonUK: false });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [paymentSettling, setPaymentSettling] = useState(false);
  const [pendingBookingId, setPendingBookingId] = useState(null);
  // Timestamp of first mount — used by the intel beacon to compute
  // `wizard_seconds`. Sub-3-second submissions get flagged.
  const pageLoadAtRef = useRef(new Date().toISOString());

  useEffect(() => {
    fetchShopData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathSubdomain]);

  // Pre-select a service if the landing page handed us `?service=<id>`,
  // and skip straight past the Services step. If the shop only has one
  // barber (or hides the chooser) we also skip Barbers. This lets the
  // Services section on the shop-front deep-link into the booking flow.
  useEffect(() => {
    // Wait for BOTH services and barbers before branching on step.
    // Without the barbers guard, this effect fires as soon as services
    // land (loadShop awaits services before barbers) and takes the
    // else branch → setStep(2). For a single-barber shop
    // (showBarberStep=false) step 2 renders nothing → blank screen.
    if (!services.length || !barbers.length) return;
    const usp = new URLSearchParams(location.search);
    const sid = usp.get('service');
    if (!sid) return;
    if (selectedService?.id === sid) return;
    const match = services.find((s) => s.id === sid);
    if (!match) return;
    setSelectedService(match);
    const allowChoose = shop?.allow_choose_barber !== false;
    if (barbers.length === 1 || !allowChoose) setStep(3);
    else setStep(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, barbers, shop, location.search]);

  useEffect(() => {
    if (selectedService && selectedBarber) generateAvailableSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, selectedBarber]);

  // Compute each barber's next available slot for the picked service.
  // Runs once when service+barbers are known (or the shop's booking
  // window changes). One barber_hours query + one bookings query.
  useEffect(() => {
    if (!selectedService || !barbers.length) return;
    computeBarberNextAvailable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, barbers]);

  // Apply accent colour to a CSS variable on this component's root so
  // the existing booking page styles can pick up the shop's brand.
  useEffect(() => {
    if (shop?.accent_colour) {
      document.documentElement.style.setProperty('--shop-accent', shop.accent_colour);
    }
    return () => {
      document.documentElement.style.removeProperty('--shop-accent');
    };
  }, [shop?.accent_colour]);

  const fetchShopData = async () => {
    setLoading(true);
    setShopNotFound(false);
    try {
      const subdomain = resolveSubdomainFromUrl({
        hostname: typeof window !== 'undefined' ? window.location.hostname : '',
        pathname: location.pathname,
        search: location.search,
        pathSubdomain,
      });

      let shopQuery = supabase.from('shops').select(SHOP_PUBLIC_COLUMNS);
      if (subdomain) shopQuery = shopQuery.eq('subdomain', subdomain).single();
      else shopQuery = shopQuery.limit(1).single();
      const { data: shopData, error: shopError } = await shopQuery;

      if (shopError || !shopData) {
        setShopNotFound(true);
        setLoading(false);
        return;
      }
      // Supplementary fetch for `booking_inherit_hero` — kept out of
      // SHOP_PUBLIC_COLUMNS so the main select doesn't 400 on shops
      // whose DB migration hasn't been applied yet. Missing column /
      // missing grant → treat as FALSE (default booking theme).
      try {
        const { data: flagRow } = await supabase
          .from('shops')
          .select('booking_inherit_hero')
          .eq('id', shopData.id)
          .maybeSingle();
        shopData.booking_inherit_hero = !!(flagRow && flagRow.booking_inherit_hero);
      } catch {
        shopData.booking_inherit_hero = false;
      }
      setShop(shopData);

      const { data: servicesData, error: servicesError } = await supabase
        .rpc('get_public_shop_services', { _shop_id: shopData.id });
      if (servicesError) throw servicesError;
      setServices(servicesData || []);

      const { data: barbersData, error: barbersError } = await supabase
        .rpc('get_public_shop_barbers', { _shop_id: shopData.id });
      if (barbersError) throw barbersError;
      setBarbers(barbersData || []);

      // If the shop hides the barber chooser, OR there's only one
      // barber, auto-select the first one.
      if (barbersData && (barbersData.length === 1 || shopData.allow_choose_barber === false)) {
        setSelectedBarber(barbersData[0]);
      }
    } catch (error) {
      console.error('Error fetching shop data:', error);
      setShopNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const generateAvailableSlots = async () => {
    if (!selectedService || !selectedBarber) return;
    setAvailabilityLoading(true);
    try {
      const { days, emptyReason: reason } = await buildDayAvailability({
        shop,
        service: selectedService,
        barber: selectedBarber,
      });
      setDayAvailability(days);
      if (reason) {
        setEmptyReason(reason);
        setSelectedDate(null);
        setAvailableSlots([]);
      } else {
        setEmptyReason(null);
        const firstAvailable = days.find((d) => d.hasSlots) || days[0];
        setSelectedDate(firstAvailable.date);
        setAvailableSlots(firstAvailable.slots);
      }
    } catch (error) {
      console.error('Error generating slots:', error);
      setDayAvailability([]);
      setSelectedDate(null);
      setAvailableSlots([]);
      setEmptyReason(null);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const computeBarberNextAvailable = async () => {
    if (!selectedService || !barbers.length) return;
    setBarberNextLoading(true);
    try {
      const next = await buildBarberNextAvailable({
        service: selectedService,
        barbers,
      });
      setBarberNext(next);
    } catch (err) {
      console.error('Error computing per-barber next-available:', err);
      setBarberNext({});
    } finally {
      setBarberNextLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    // Match by local YYYY-MM-DD to stay in step with the DST-safe
    // `d.key` produced by buildDayAvailability. Comparing via
    // `toISOString().slice(0,10)` here was off-by-one on any BST day.
    const day = dayAvailability.find((d) => d.key === dateKey(date));
    if (!day) return;
    setSelectedDate(day.date);
    setAvailableSlots(day.slots);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    const allowChoose = shop?.allow_choose_barber !== false;
    setStep(barbers.length === 1 || !allowChoose ? 3 : 2);
  };

  const handleBarberSelect = (barber) => {
    setSelectedBarber(barber);
    setStep(3);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(4);
  };

  // "Next available" shortcut: jump to the earliest bookable slot
  // across the 14-day window in a single click (proven conversion
  // booster on booking widgets).
  const handlePickNextAvailable = () => {
    const firstDay = dayAvailability.find((d) => d.hasSlots && d.slots.length > 0);
    if (!firstDay) return;
    setSelectedDate(firstDay.date);
    setAvailableSlots(firstDay.slots);
    setSelectedSlot(firstDay.slots[0]);
    setStep(4);
  };

  const handlePaymentSuccess = async () => {
    // stripe.confirmPayment resolved without error, so Stripe took
    // the customer's card successfully. We still need the server to
    // reconcile the booking row against Stripe before showing the
    // confirmation page — the webhook is authoritative but arrives on
    // its own schedule; /payment/confirm re-reads the PI now and flips
    // the row synchronously. iteration_52 caught this call silently
    // 500'ing (row pointed at an orphaned PI) while the UI still said
    // "Confirmed" — a paid customer with a pending booking.
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    let serverConfirmed = false;
    try {
      const res = await fetch(`${BACKEND_URL}/api/stripe/payment/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: pendingBookingId }),
      });
      if (res.ok) {
        const data = await res.json();
        serverConfirmed = !!(data && data.success);
      }
    } catch (confirmError) {
      console.error('Error confirming payment server-side:', confirmError);
    }

    if (!serverConfirmed) {
      setPaymentSettling(true);
      return;
    }

    let emailSent = true;
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/send-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: pendingBookingId }),
      });
      if (res.ok) {
        const j = await res.json().catch(() => ({}));
        emailSent = j.sent !== false;
      } else {
        emailSent = false;
      }
    } catch (emailError) {
      console.error('Error sending confirmation email:', emailError);
      emailSent = false;
    }

    setConfirmedBooking({
      service: selectedService.name,
      barber: selectedBarber.name,
      time: selectedSlot.display,
      date: selectedSlot.datetime,
      customerName: customerData.name,
      customerEmail: customerData.email,
      emailSent,
    });
    setBookingConfirmed(true);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();

    if (!customerData.isNonUK && !isValidUKMobile(customerData.phone)) {
      alert('Enter a UK mobile as 07xxx xxxxxx, or tick "Non-UK number".');
      return;
    }

    setSubmitting(true);
    try {
      const slotEnd = new Date(selectedSlot.datetime);
      slotEnd.setMinutes(slotEnd.getMinutes() + selectedService.duration_min);

      // Proper interval overlap check (matches DB EXCLUDE constraint):
      //   existing.starts_at < newEnd  AND  existing.ends_at > newStart
      // Include 'pending' so deposit-in-flight bookings still block the slot.
      const { data: conflictCheck, error: conflictError } = await supabase
        .from('bookings')
        .select('id')
        .eq('barber_id', selectedBarber.id)
        .in('status', ['pending', 'confirmed', 'completed'])
        .lt('starts_at', slotEnd.toISOString())
        .gt('ends_at', selectedSlot.datetime.toISOString());
      if (conflictError) throw conflictError;
      if (conflictCheck && conflictCheck.length > 0) {
        alert('Sorry, this slot is no longer available. Please choose another time.');
        setStep(3);
        generateAvailableSlots();
        return;
      }

      const { data: rpcCustomerId, error: rpcCustomerError } = await supabase
        .rpc('find_or_create_customer', {
          _shop_id: shop.id,
          _name: customerData.name,
          _email: customerData.email || '',
          _phone: customerData.phone || '',
          // SEC-002 fix: non-UK flag now lives on the SECURITY DEFINER
          // RPC. Anon no longer has raw UPDATE on customers.
          _is_non_uk_number: !!customerData.isNonUK,
        });
      if (rpcCustomerError) throw rpcCustomerError;
      const customerId = rpcCustomerId;

      const cancellationToken = Math.random().toString(36).substring(2) + Date.now().toString(36);

      const { amount: depositAmount, required: depositRequired } =
        resolveDeposit(selectedService, shop);

      // SEC-002 fix: booking creation now goes through the locked
      // `create_public_booking` RPC. That function ignores any
      // status / deposit_paid / payment_status the browser might
      // send, recomputes the deposit from service+shop server-side,
      // and validates every foreign key belongs to the same shop.
      // The DB-level exclusion constraint still surfaces 23P01 for
      // slot races.
      const { data: newBookingId, error: bookingError } = await supabase
        .rpc('create_public_booking', {
          _shop_id: shop.id,
          _customer_id: customerId,
          _barber_id: selectedBarber.id,
          _service_id: selectedService.id,
          _starts_at: selectedSlot.datetime.toISOString(),
          _cancellation_token: cancellationToken,
        });

      if (bookingError) {
        // 23P01 = Postgres exclusion_violation. Two customers raced for
        // the same slot; the DB constraint rejected the loser.
        // The RPC re-raises via RAISE EXCEPTION → surfaces as a
        // PostgREST error whose message contains "conflicting key value"
        // or the 23P01 SQLSTATE.
        const msg = String(bookingError.message || '');
        if (bookingError.code === '23P01' || msg.includes('23P01') || msg.toLowerCase().includes('conflicting')) {
          alert('Sorry, this slot was just taken. Please choose another time.');
          setStep(3);
          generateAvailableSlots();
          return;
        }
        // Any other DB error is an internal problem — surface a
        // customer-safe message and log the raw detail to the
        // console. Never leak Postgres error text (schema names,
        // enum names, RLS policy names) to the customer's alert.
        // Original testing-agent finding: 42804 enum casts leaked
        // "column \"status\" is of type booking_status" into a
        // customer-facing dialog.
        console.error('Booking creation failed:', bookingError);
        alert("Something went wrong while creating your booking. Please try again in a moment, or contact the shop directly if this keeps happening.");
        return;
      }

      // The RPC returns the new booking id directly (uuid, no .id).
      const newBooking = { id: newBookingId };

      // Fire the intel beacon (best-effort, non-blocking). Server
      // captures IP + UA from headers; client supplies wizard timing,
      // UTM tags, screen dims, timezone offset, language. Beacon
      // auth is the cancellation_token — unforgeable, only this
      // browser has it.
      try {
        const usp = new URLSearchParams(window.location.search);
        const payload = {
          booking_id: newBooking.id,
          cancellation_token: cancellationToken,
          page_load_at: pageLoadAtRef.current,
          utm_source:   usp.get('utm_source')   || undefined,
          utm_medium:   usp.get('utm_medium')   || undefined,
          utm_campaign: usp.get('utm_campaign') || undefined,
          referrer:     document.referrer || undefined,
          screen_w:     window.innerWidth  || undefined,
          screen_h:     window.innerHeight || undefined,
          timezone_offset_min: -new Date().getTimezoneOffset(),
          language:     navigator.language || undefined,
        };
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/context`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      } catch (_) { /* never let intel break bookings */ }

      if (depositRequired && depositAmount > 0) {
        setPendingBookingId(newBooking.id);
        setStep(5);
        setSubmitting(false);
        return;
      }

      // Fire the confirmation email and capture whether the provider
      // actually accepted it, so the success screen can gate its
      // "we've sent you an email" copy on real delivery (test iter62).
      let emailSent = true;
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${BACKEND_URL}/api/email/send-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking_id: newBooking.id }),
        });
        if (res.ok) {
          const j = await res.json().catch(() => ({}));
          emailSent = j.sent !== false;
        } else {
          emailSent = false;
        }
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        emailSent = false;
      }

      setConfirmedBooking({
        service: selectedService.name,
        barber: selectedBarber.name,
        time: selectedSlot.display,
        date: selectedSlot.datetime,
        customerName: customerData.name,
        customerEmail: customerData.email,
        emailSent,
      });
      setBookingConfirmed(true);
    } catch (error) {
      // Fallback for anything the specific bookingError branch didn't
      // catch (e.g. network / RPC infrastructure error). Same rule:
      // never leak internal detail to the customer.
      console.error('Error creating booking:', error);
      alert("Something went wrong while creating your booking. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="booking-loading">Loading...</div>;

  if (shopNotFound || !shop) {
    return (
      <div className="booking-loading" data-testid="booking-shop-not-found">
        <h2 style={{ marginBottom: 8 }}>Shop not found</h2>
        <p>Check the link and try again.</p>
      </div>
    );
  }

  // Branding helpers
  const themeSlug = normaliseTheme(shop.theme);
  const accent = resolveAccent(shop.theme, shop.accent_colour);
  const themeClass = `theme-${themeSlug}`;
  // Hero background priority: `hero_image_url` (Shop profile > Hero
  // image) → first gallery image → plain accent colour. Gated by the
  // `booking_inherit_hero` flag — when FALSE the booking page falls
  // back to the theme's own top block (accent / black) regardless of
  // whether a hero is uploaded for the landing page. When a hero IS
  // used, `hero_opacity` (0.1..1.0) is honoured via a white overlay
  // layer stacked on top of the dark scrim.
  const inheritHero = shop.booking_inherit_hero === true;
  const heroUrl = inheritHero
    ? (shop.hero_image_url || (shop.gallery_urls && shop.gallery_urls[0]) || null)
    : null;
  const heroIsUpload = inheritHero && !!shop.hero_image_url;
  const heroOp = Math.max(0.1, Math.min(1.0,
    typeof shop.hero_opacity === 'number' ? shop.hero_opacity : 1.0));
  const heroStyle = heroUrl
    ? {
        backgroundImage: [
          heroIsUpload ? `linear-gradient(rgba(255,255,255,${1 - heroOp}), rgba(255,255,255,${1 - heroOp}))` : null,
          'linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.45))',
          `url(${heroUrl})`,
        ].filter(Boolean).join(', '),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: accent,
      }
    : { background: accent };
  const logoStyle = shop.logo_url
    ? { backgroundImage: `url(${shop.logo_url})`, backgroundSize: 'cover', backgroundPosition: 'center', color: 'transparent' }
    : {};
  const pageStyle = { '--acc': accent, '--acc-ink': accent };
  const showBarberStep = barbers.length > 1 && shop.allow_choose_barber !== false;

  if (paymentSettling) {
    return <SettlingScreen shop={shop} themeClass={themeClass} pageStyle={pageStyle} heroStyle={heroStyle} logoStyle={logoStyle} />;
  }

  if (bookingConfirmed && confirmedBooking) {
    return (
      <ConfirmedScreen
        shop={shop}
        themeClass={themeClass}
        pageStyle={pageStyle}
        heroStyle={heroStyle}
        logoStyle={logoStyle}
        booking={confirmedBooking}
        onReset={() => {
          setBookingConfirmed(false);
          setConfirmedBooking(null);
          setStep(1);
          setSelectedService(null);
          setSelectedBarber((barbers.length === 1 || shop.allow_choose_barber === false) ? barbers[0] : null);
          setSelectedSlot(null);
          setCustomerData({ name: '', phone: '', email: '' });
        }}
      />
    );
  }

  return (
    <div className={`booking-page ${themeClass}`} style={pageStyle} data-testid="booking-page">
      <BookingHeader shop={shop} heroStyle={heroStyle} logoStyle={logoStyle} showWelcome />
      <div className="booking-container">
        <StepsHeader step={step} showBarberStep={showBarberStep} />

        {step === 1 && (
          <ServicesStep services={services} onSelect={handleServiceSelect} />
        )}

        {step === 2 && showBarberStep && (
          <BarbersStep
            barbers={barbers}
            barberNext={barberNext}
            barberNextLoading={barberNextLoading}
            onSelect={handleBarberSelect}
            onBack={() => setStep(1)}
          />
        )}

        {step === 3 && (
          <TimeStep
            barber={selectedBarber}
            showBarberStep={showBarberStep}
            availabilityLoading={availabilityLoading}
            emptyReason={emptyReason}
            dayAvailability={dayAvailability}
            selectedDate={selectedDate}
            availableSlots={availableSlots}
            onBack={() => setStep(showBarberStep ? 2 : 1)}
            onDateSelect={handleDateSelect}
            onSlotSelect={handleSlotSelect}
            onPickNextAvailable={handlePickNextAvailable}
          />
        )}

        {step === 4 && (
          <DetailsStep
            service={selectedService}
            barber={selectedBarber}
            slot={selectedSlot}
            customerData={customerData}
            setCustomerData={setCustomerData}
            submitting={submitting}
            shop={shop}
            onBack={() => setStep(3)}
            onSubmit={handleSubmitBooking}
          />
        )}

        {/* Step 5: Payment (if deposit required) */}
        {step === 5 && pendingBookingId && (
          <PaymentStep
            booking={{
              bookingId: pendingBookingId,
              depositAmount: resolveDeposit(selectedService, shop || {}).amount,
              fullAmount: Number(selectedService.price || 0),
              allowPayInFull: !!(resolveDeposit(selectedService, shop || {}).allowPayInFull),
              serviceName: selectedService.name,
              barberName: selectedBarber.name,
              time: selectedSlot.display,
            }}
            onSuccess={handlePaymentSuccess}
            onBack={() => setStep(4)}
          />
        )}
      </div>
    </div>
  );
};

export default Booking;
